import { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { createCriterion, updateCriterion } from 'slices/scoringSlice'

interface CriterionFormProps {
	open: boolean
	onClose: () => void
	evalId: string
	criterion?: any
	defaultSortOrder?: number
}

const CriterionForm = ({ open, onClose, evalId, criterion, defaultSortOrder = 0 }: CriterionFormProps) => {
	const dispatch = useDispatch()
	const isEdit = Boolean(criterion)

	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [weight, setWeight] = useState('')
	const [category, setCategory] = useState('')
	const [sortOrder, setSortOrder] = useState(0)
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (open) {
			setName(criterion?.name || '')
			setDescription(criterion?.description || '')
			setWeight(criterion?.weight != null ? String(criterion.weight) : '')
			setCategory(criterion?.category || '')
			setSortOrder(criterion?.sortOrder ?? defaultSortOrder)
			setSubmitting(false)
			setError(null)
		}
	}, [open, criterion, defaultSortOrder])

	const handleSubmit = useCallback(async () => {
		const weightNum = Number(weight)
		if (!name.trim()) {
			setError('Name is required')
			return
		}
		if (!Number.isFinite(weightNum) || weightNum < 0 || weightNum > 100) {
			setError('Weight must be a number between 0 and 100')
			return
		}
		setError(null)
		setSubmitting(true)
		const payload = {
			name: name.trim(),
			description: description.trim() || undefined,
			weight: weightNum,
			category: category.trim() || undefined,
			sortOrder,
		}
		try {
			if (isEdit) {
				await (dispatch as any)(updateCriterion({ evalId, criterionId: criterion._id, payload }))
			} else {
				await (dispatch as any)(createCriterion({ evalId, payload }))
			}
			onClose()
		} finally {
			setSubmitting(false)
		}
	}, [dispatch, evalId, criterion, isEdit, name, description, weight, category, sortOrder, onClose])

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>{isEdit ? 'Edit Criterion' : 'Add Criterion'}</DialogTitle>
					<DialogDescription>
						Criteria are weighted factors used to score each site. Total weight across criteria
						should sum to 100.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 pt-2">
					<div>
						<Label htmlFor="critName" className="mb-1.5 block">Name *</Label>
						<Input id="critName" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Location Quality" />
					</div>
					<div>
						<Label htmlFor="critDesc" className="mb-1.5 block">Description</Label>
						<Input id="critDesc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional details" />
					</div>
					<div className="grid grid-cols-12 gap-3">
						<div className="col-span-5">
							<Label htmlFor="critWeight" className="mb-1.5 block">Weight * (0-100)</Label>
							<Input
								id="critWeight"
								type="number"
								min={0}
								max={100}
								value={weight}
								onChange={(e) => setWeight(e.target.value)}
								placeholder="e.g. 25"
							/>
						</div>
						<div className="col-span-7">
							<Label htmlFor="critCat" className="mb-1.5 block">Category</Label>
							<Input
								id="critCat"
								value={category}
								onChange={(e) => setCategory(e.target.value)}
								placeholder="e.g. Market, Financial"
							/>
						</div>
					</div>

					{error && <p className="text-xs text-red-500">{error}</p>}

					<div className="flex justify-end gap-2 pt-2">
						<Button variant="ghost" onClick={onClose}>Cancel</Button>
						<Button variant="primary" onClick={handleSubmit} disabled={submitting}>
							{submitting ? 'Saving...' : isEdit ? 'Update' : 'Add'}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default CriterionForm
